import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Mail, Phone, Check, Loader2, Send, RefreshCw } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { formatApiError } from "@/pages/AdminLogin";
import { InquiryNotes } from "@/components/admin/InquiryNotes";
import { ChangePassword } from "@/components/admin/ChangePassword";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ withCredentials: true });
const STATUS = { neu: "Neu", in_bearbeitung: "In Bearbeitung", erledigt: "Erledigt" };
const FILTERS = [["alle", "Alle"], ...Object.entries(STATUS)];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }) : "");

const Inquiry = ({ q, onStatus, onConfirm, onNotes, busy, unread }) => (
  <article data-testid={`inquiry-${q.id}`} className={`relative grid gap-6 border-t border-[var(--line)] py-8 transition-[background-color] duration-700 lg:grid-cols-12 ${unread ? "-mx-4 bg-[#E3A68F]/20 px-4 sm:-mx-6 sm:px-6" : ""}`}>
    {unread && <span className="absolute left-0 top-0 h-full w-1 bg-[var(--clay)]" />}
    <div className="lg:col-span-4">
      <div className="flex flex-wrap items-center gap-2">
        {unread && <span className="font-mono-d rounded-full bg-[var(--ink)] px-3 py-1 text-[11px] uppercase tracking-wider text-[var(--stone)]" data-testid={`inquiry-unread-${q.id}`}>Ungelesen</span>}
        <span className={`font-mono-d rounded-full px-3 py-1 text-[11px] uppercase tracking-wider ${q.status === "neu" ? "bg-[var(--clay)] text-[var(--stone)]" : q.status === "erledigt" ? "bg-[var(--ink)]/10 text-[var(--ink-2)]" : "bg-[#E3A68F]/40 text-[var(--ink)]"}`} data-testid={`inquiry-status-${q.id}`}>{STATUS[q.status] || "Neu"}</span>
        <span className="font-mono-d text-xs text-[var(--ink-2)]">{fmt(q.created_at)}</span>
      </div>
      <h3 className="font-serif-d mt-4 text-3xl leading-tight">{q.name}</h3>
      {q.company && <p className="text-[var(--ink-2)]">{q.company}</p>}
      <div className="mt-4 space-y-1 text-sm">
        <a href={`mailto:${q.email}`} className="flex items-center gap-2 hover:text-[var(--clay)]"><Mail className="h-4 w-4" />{q.email}</a>
        {q.phone && <a href={`tel:${q.phone}`} className="flex items-center gap-2 hover:text-[var(--clay)]"><Phone className="h-4 w-4" />{q.phone}</a>}
      </div>
      {q.timeframe && <p className="font-mono-d mt-4 text-xs uppercase tracking-wider text-[var(--clay-2)]">Übergabe: {q.timeframe}</p>}
    </div>
    <div className="lg:col-span-5">
      <p className="whitespace-pre-line text-base leading-relaxed text-[var(--ink)]">{q.message}</p>
      <InquiryNotes q={q} onChange={(notes) => onNotes(q.id, notes)} />
    </div>
    <div className="flex flex-col gap-2 lg:col-span-3">
      {q.confirmation_sent_at ? (
        <p className="flex items-center gap-2 text-sm text-[var(--ink-2)]" data-testid={`inquiry-confirmed-${q.id}`}><Check className="h-4 w-4 text-[var(--clay)]" /> Bestätigt am {fmt(q.confirmation_sent_at)}</p>
      ) : (
        <button onClick={() => onConfirm(q.id)} disabled={busy === q.id} data-testid={`inquiry-confirm-${q.id}`} className="flex items-center justify-center gap-2 rounded-full bg-[var(--ink)] px-4 py-2.5 text-sm text-[var(--stone)] transition-[background-color] duration-300 hover:bg-[var(--clay)] disabled:opacity-60">
          {busy === q.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Eingangsbestätigung senden
        </button>
      )}
      <div className="flex flex-wrap gap-2">
        {Object.entries(STATUS).map(([k, label]) => (
          <button key={k} onClick={() => onStatus(q.id, k)} data-testid={`inquiry-set-${k}-${q.id}`} className={`rounded-full border px-3 py-1.5 text-xs transition-[background-color,border-color] duration-300 ${q.status === k ? "border-[var(--ink)] bg-[var(--ink)]/5" : "border-[var(--ink)]/15 hover:border-[var(--ink)]/50"}`}>{label}</button>
        ))}
      </div>
    </div>
  </article>
);

export default function Admin() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("alle");
  const [busy, setBusy] = useState(null);
  const [unread, setUnread] = useState(() => new Set());

  const markSeen = useCallback((list) => {
    const fresh = list.filter((q) => !q.seen_at).map((q) => q.id);
    if (fresh.length) {
      setUnread((p) => new Set([...p, ...fresh]));
      ax.post(`${API}/admin/inquiries/mark-seen`).catch(() => {});
    }
  }, []);

  const load = useCallback(async () => {
    const apply = (data) => { const list = Array.isArray(data) ? data : []; setItems(list); markSeen(list); };
    try {
      const { data } = await ax.get(`${API}/admin/inquiries`);
      apply(data);
    } catch (err) {
      if (err.response?.status === 401) {
        try { await ax.post(`${API}/auth/refresh`); const { data } = await ax.get(`${API}/admin/inquiries`); apply(data); }
        catch { nav("/admin/login"); }
      } else toast.error(formatApiError(err.response?.data?.detail));
    }
  }, [nav, markSeen]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await ax.patch(`${API}/admin/inquiries/${id}`, { status }); setItems((p) => p.map((q) => (q.id === id ? { ...q, status } : q))); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const confirm = async (id) => {
    setBusy(id);
    try {
      const { data } = await ax.post(`${API}/admin/inquiries/${id}/confirm`);
      setItems((p) => p.map((q) => (q.id === id ? { ...q, confirmation_sent_at: data?.confirmation_sent_at } : q)));
      toast.success("Eingangsbestätigung wurde gesendet.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const setNotes = (id, notes) => setItems((p) => p.map((q) => (q.id === id ? { ...q, notes } : q)));

  const logout = async () => { await ax.post(`${API}/auth/logout`).catch(() => {}); nav("/admin/login"); };

  const list = useMemo(() => (items || []).filter((q) => filter === "alle" || (q.status || "neu") === filter), [items, filter]);
  const newCount = (items || []).filter((q) => (q.status || "neu") === "neu").length;

  return (
    <div className="min-h-screen bg-[var(--stone)]" data-testid="admin-page">
      <header className="border-b border-[var(--line)]">
        <div className="mx-auto flex h-[72px] max-w-[1440px] items-center justify-between px-5 sm:px-8 lg:px-12">
          <Link to="/" data-testid="admin-home-link"><Logo /></Link>
          <div className="flex items-center gap-5">
            <span className="flex items-center gap-2 text-sm text-[var(--ink-2)]" data-testid="admin-unread-badge">
              <span className="hidden sm:inline">Ungelesen</span>
              <span className={`grid h-6 min-w-6 place-items-center rounded-full px-2 text-xs font-medium ${unread.size ? "bg-[var(--clay)] text-[var(--stone)]" : "bg-[var(--ink)]/10 text-[var(--ink-2)]"}`}>{unread.size}</span>
            </span>
            <ChangePassword />
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 text-sm text-[var(--ink-2)] hover:text-[var(--clay)]"><LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Abmelden</span></button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[1440px] px-5 py-14 sm:px-8 lg:px-12 lg:py-20">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow">Intern</p>
            <h1 className="font-serif-d mt-4 text-5xl tracking-tight sm:text-6xl">Anfragen</h1>
            <p className="mt-3 text-[var(--ink-2)]" data-testid="admin-summary">{items ? `${items.length} insgesamt · ${newCount} neu · ${unread.size} seit Ihrem letzten Besuch` : "Wird geladen…"}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {FILTERS.map(([k, label]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`admin-filter-${k}`} className={`rounded-full border px-4 py-2 text-sm transition-[background-color,color] duration-300 ${filter === k ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--stone)]" : "border-[var(--ink)]/20 hover:border-[var(--ink)]/50"}`}>{label}</button>
            ))}
            <button onClick={load} aria-label="Aktualisieren" data-testid="admin-refresh" className="rounded-full border border-[var(--ink)]/20 p-2.5 hover:border-[var(--ink)]/50"><RefreshCw className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="mt-12" data-testid="admin-inquiry-list">
          {items && list.length === 0 && <p className="border-t border-[var(--line)] py-12 text-[var(--ink-2)]" data-testid="admin-empty">Keine Anfragen in dieser Ansicht.</p>}
          {list.map((q) => <Inquiry key={q.id} q={q} onStatus={setStatus} onConfirm={confirm} onNotes={setNotes} busy={busy} unread={unread.has(q.id)} />)}
        </div>
      </main>
    </div>
  );
}
